import { reportErrorToConsole } from '@applyonline/shared'
import { FORM_ID, ScrollableAlert, useTranslatedOptionEnum } from '@applyonline/shared/components'
import { removeSinFormat, sinFormat, useFormAutoSave } from '@applyonline/shared/utils'
import { yupResolver } from '@hookform/resolvers/yup'
import { EBeneficiaryType, EGender, ELanguage, ELoanPurpose, getERelationToApplicantList } from '@if/core/types'
import { range } from 'lodash-es'
import { ChangeEvent, useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import ValidationErrorMessage from '../../../../../packages/shared/validator/ValidationErrorMessage'
import { useTrackFunnelStepReached } from '../../../services/trackingEvents'
import { Constants } from '../../../types/constants'
import { PersonalInformationInput, PersonalInformationInputSchema } from '../../../types/Schemas/PrequalificationSchema'
import { ETrackedEvents } from '../../../types/tracking'

interface Props {
  prequalificationData: PersonalInformationInput
  isCoapplicant?: boolean
  onPrequalificationChanged: (data: PersonalInformationInput) => void
  nextStepAfter: (currentStep: string) => void
  currentStep: string
  loanPurposeId: ELoanPurpose
}

const fixNumberInTwoDigits = (num: number) => {
  return (num < 10 ? '0' : '') + num
}


const PersonalInformationForm = ({
  isCoapplicant = false,
  prequalificationData,
  onPrequalificationChanged,
  nextStepAfter,
  currentStep,
  loanPurposeId,
}: Props) => {
  const { t, i18n } = useTranslation()

  useTrackFunnelStepReached(ETrackedEvents.PersonalInformation, useParams().merchantId)

  const [birthDate, setBirthDate] = useState<Record<string, number | undefined>>({
    year: undefined,
    month: undefined,
    day: undefined,
  })

  const [daysInMonth, setDaysInMonth] = useState(31)

  const validationSchema = PersonalInformationInputSchema()
  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    watch,
    getValues,
    subscribe,
  } = useForm<PersonalInformationInput>({
    mode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: prequalificationData,
  })

  useFormAutoSave(subscribe, onPrequalificationChanged)

  const watchBirthDate = watch('applicant.birthDate')
  const watchBeneficiaryTypeId = watch('beneficiaryTypeId')
  const isVeterinary = loanPurposeId === ELoanPurpose.Veterinary
  const isMedical =
    loanPurposeId === ELoanPurpose.Medical ||
    loanPurposeId === ELoanPurpose.Aesthetics ||
    loanPurposeId === ELoanPurpose.Dentistry

  useEffect(() => {
    const d = watchBirthDate?.split('-') ?? ['', '', '']
    if (watchBirthDate)
      setBirthDate({
        year: Number(d[0]),
        month: Number(d[1]),
        day: Number(d[2]),
      })
  }, [watchBirthDate])

  const handleDate = (event: ChangeEvent<HTMLSelectElement>) => {
    setBirthDate((birth) => {
      const { value } = event.target
      switch (event.target.id) {
        case 'month':
          birth.month = +value
          break
        case 'year':
          birth.year = +value
          break
        default:
          birth.day = +value
      }

      if (birth.year !== undefined && birth.month !== undefined) {
        const days = new Date(+birth.year, birth.month, 0).getDate()
        setDaysInMonth(days)
        if (birth.day !== undefined && birth.day > days) {
          birth.day = undefined
        }
      }

      if (!Object.values(birth).includes(undefined)) {
        setValue(
          'applicant.birthDate',
          `${birth.year}-${fixNumberInTwoDigits(Number(birth.month))}-${fixNumberInTwoDigits(Number(birth.day))}`,
        )
      } else {
        setValue('applicant.birthDate', '')
      }

      return birth
    })
  }

  const beneficiaryRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue('beneficiaryTypeId', +event.target.value)
    errors.beneficiaryTypeId = undefined
  }

  const currentDate = new Date()

  const minBirthDate = {
    year: currentDate.getFullYear() - 18,
    month: currentDate.getMonth() + 1,
    day: currentDate.getDate(),
  }

  const maxBirthDate = {
    year: currentDate.getFullYear() - 100,
    month: 1,
    day: 1,
  }

  const handleSinOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    const sin = removeSinFormat(value)
    setValue('applicant.sin', sin, { shouldValidate: true })
  }

  useEffect(() => {
    if (isVeterinary || watchBeneficiaryTypeId === EBeneficiaryType.Other) {
      setValue('beneficiaryTypeId', EBeneficiaryType.Other)
    } else {
      setValue('beneficiaryTypeId', watchBeneficiaryTypeId)
      setValue('otherBeneficiaryFirstName', '')
      setValue('otherBeneficiaryLastName', '')
    }
  }, [watchBeneficiaryTypeId, isVeterinary, setValue])

  const relationToApplicantOptions = useTranslatedOptionEnum(
    'ERelationToApplicant',
    getERelationToApplicantList(),
    'enum',
  )

  const onSubmit: SubmitHandler<PersonalInformationInput> = (data) => {
    onPrequalificationChanged(data)
    nextStepAfter(currentStep)
  }

  return (
    <section className="step-content">
      <ScrollableAlert
        showAlert={
          !!(
            errors.applicant?.birthDate &&
            errors.applicant.birthDate.type !== 'required' &&
            errors.applicant.birthDate.message
          )
        }
        type="error"
        message={errors.applicant?.birthDate?.message || ''}
      />
      <h3 className="form-question">
        {isCoapplicant ? t('personalInformation.aboutCoapplicant') : t('personalInformation.aboutYourself')}
      </h3>

      <form onSubmit={handleSubmit(onSubmit, reportErrorToConsole)} id={FORM_ID}>
        <div className={`control-group ${errors.applicant?.languageId && 'error'}`}>
          <label htmlFor="radiobuttons">{t('personalInformation.preferredLanguage')}</label>
          <div className="radiobuttons-wrap">
            <div className="radio-wrap">
              <input
                type="radio"
                value={ELanguage.French}
                id="French"
                {...register('applicant.languageId')}
                defaultChecked={prequalificationData.applicant.languageId === ELanguage.French}
              />
              <label htmlFor="French">{t(`ELanguage.${ELanguage.French}`, { ns: 'enum' })}</label>
            </div>
            <div className="radio-wrap">
              <input
                type="radio"
                value={ELanguage.English}
                id="English"
                {...register('applicant.languageId')}
                defaultChecked={prequalificationData.applicant.languageId === ELanguage.English}
              />
              <label htmlFor="English">{t(`ELanguage.${ELanguage.English}`, { ns: 'enum' })}</label>
            </div>
          </div>
        </div>

        <div className={`control-group ${errors.applicant?.genderId && 'error'}`}>
          <label htmlFor="radiobuttons">{t('personalInformation.yourGender')}</label>
          <div className="radiobuttons-wrap">
            <div className="radio-wrap">
              <input
                type="radio"
                value={0}
                id="Male"
                {...register('applicant.genderId')}
                defaultChecked={prequalificationData.applicant.genderId === EGender.Men}
              />
              <label htmlFor="Male">{t('personalInformation.male')}</label>
            </div>
            <div className="radio-wrap">
              <input
                type="radio"
                value={1}
                id="Female"
                {...register('applicant.genderId')}
                defaultChecked={prequalificationData.applicant.genderId === EGender.Woman}
              />
              <label htmlFor="Female">{t('personalInformation.female')}</label>
            </div>
            <div className="radio-wrap">
              <input
                type="radio"
                value={2}
                id="Other"
                {...register('applicant.genderId')}
                defaultChecked={prequalificationData.applicant.genderId === EGender.Other}
              />
              <label htmlFor="Other"> {t('personalInformation.preferNotSay')}</label>
            </div>
          </div>
        </div>

        <div className="control-group-wrap">
          <div className={`control-group first-name third sm-full ${errors.applicant?.firstName && 'error'}`}>
            <label htmlFor="firstName">
              {t('personalInformation.yourFirstName')}
              <span className="form-hint">{t('personalInformation.nameHint')}</span>
            </label>
            <input
              type="text"
              id="firstName"
              {...register('applicant.firstName')}
              maxLength={50}
              autoComplete="given-name"
            />
            <ValidationErrorMessage message={errors?.applicant?.firstName?.message} />
          </div>

          <div className={`control-group last-name third sm-full ${errors.applicant?.lastName && 'error'}`}>
            <label htmlFor="lastName">
              {t('personalInformation.yourLastName')}
              <span className="form-hint">{t('personalInformation.nameHint')}</span>
            </label>
            <input
              type="text"
              id="lastName"
              {...register('applicant.lastName')}
              maxLength={50}
              autoComplete="family-name"
            />
            <ValidationErrorMessage message={errors?.applicant?.lastName?.message} />
          </div>

          <div className={`control-group middle-name third sm-full ${errors.applicant?.middleName && 'error'}`}>
            <label htmlFor="middleName">
              {t('personalInformation.yourMiddleName')}
              <span className="form-hint">{t('personalInformation.optional')}</span>
            </label>
            <input
              type="text"
              id="middleName"
              {...register('applicant.middleName')}
              maxLength={50}
              autoComplete='new-password'
              data-Lpignore='true'
              data-1p-ignore='true'
            />
            <ValidationErrorMessage message={errors?.applicant?.middleName?.message} />
          </div>
        </div>

        <div className="control-group">
          <label htmlFor="year">{t('personalInformation.yourBirthDate')}</label>
          <div className="date-wrap">
            <div className={`dropdown-wrap ${errors.applicant?.birthDate && 'error'}`}>
              <select id="year" name="date" onChange={handleDate} value={birthDate.year?.toString() ?? ''}>
                <option value="" disabled>
                  {t('personalInformation.birthYear')}
                </option>
                {range(minBirthDate.year, maxBirthDate.year).map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <div className={`dropdown-wrap ${errors.applicant?.birthDate && 'error'}`}>
              <select
                id="month"
                name="date"
                onChange={handleDate}
                value={birthDate.month ? fixNumberInTwoDigits(birthDate.month) : ''}
              >
                <option value="" disabled>
                  {t('personalInformation.birthMonth')}
                </option>
                {Object.keys(Constants.monthNames).map((month) => (
                  <option value={fixNumberInTwoDigits(+month)} key={Constants.monthNames[Number(month)].nameEn}>
                    {i18n.language === 'en'
                      ? Constants.monthNames[Number(month)].nameEn
                      : Constants.monthNames[Number(month)].nameFr}
                  </option>
                ))}
              </select>
            </div>
            <div className={`dropdown-wrap ${errors.applicant?.birthDate && 'error'}`}>
              <select
                id="day"
                name="date"
                onChange={handleDate}
                value={birthDate.day ? fixNumberInTwoDigits(birthDate.day) : ''}
              >
                <option value="" disabled>
                  {t('personalInformation.birthDay')}
                </option>
                {range(1, daysInMonth + 1).map((item) => (
                  <option key={item} value={fixNumberInTwoDigits(item)}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className={`control-group  ${errors.applicant?.sin && 'error'}`}>
          <label htmlFor="insurance-number">
            {t('personalInformation.sin')}
            <span className="help-tip">
              <p>{t('personalInformation.sin')}</p>
            </span>
          </label>
          <input
            id="insurance-number"
            placeholder="XXX-XXX-XXX"
            className="sin-input"
            type="text"
            inputMode="numeric"
            defaultValue={sinFormat(getValues('applicant.sin'))}
            onBlur={handleSinOnChange}
          />
          <ValidationErrorMessage message={errors?.applicant?.sin?.message} />
        </div>
        {!isCoapplicant && isVeterinary && (
          <div className={`control-group mid  ${errors.otherBeneficiaryLastName && 'error'}`}>
            <label htmlFor="otherBeneficiaryLastName">{t('loan.petName')}</label>
            <input type="text" id="otherBeneficiaryLastName" {...register('otherBeneficiaryLastName')} maxLength={50} />
          </div>
        )}
        {!isCoapplicant && isMedical && (
          <div className="control-group">
            <div id="for-whom-radiobuttons" className="radiobuttons-wrap">
              <div className="radio-wrap">
                {!isCoapplicant && (
                  <>
                    <input
                      type="radio"
                      value={EBeneficiaryType.Applicant}
                      onChange={beneficiaryRadioChange}
                      checked={watchBeneficiaryTypeId === EBeneficiaryType.Applicant}
                      id="forMe"
                    />
                    <label htmlFor="forMe">{t('loan.forMe')}</label>
                  </>
                )}

                {isCoapplicant && (
                  <>
                    <input
                      type="radio"
                      value={EBeneficiaryType.Coapplicant}
                      onChange={beneficiaryRadioChange}
                      checked={watchBeneficiaryTypeId === EBeneficiaryType.Coapplicant}
                      id="forMe"
                    />
                    <label htmlFor="forMe">{t('loan.forMe')}</label>
                  </>
                )}
              </div>
              <div className="radio-wrap">
                <input
                  type="radio"
                  value={EBeneficiaryType.Other}
                  onChange={beneficiaryRadioChange}
                  checked={watchBeneficiaryTypeId === EBeneficiaryType.Other}
                  id="forSomeOneElse"
                />
                <label htmlFor="forSomeOneElse">{t('loan.forSomeoneElse')}</label>
              </div>
            </div>
            {watchBeneficiaryTypeId === EBeneficiaryType.Other && (
              <div className="beneficiary-info" style={{ marginTop: '10px' }}>
                <div className={`control-group mid  ${errors.otherBeneficiaryFirstName && 'error'}`}>
                  <label htmlFor="otherBeneficiaryFirstName">{t('loan.otherBeneficiaryFirstName')}</label>
                  <input
                    type="text"
                    id="otherBeneficiaryFirstName"
                    {...register('otherBeneficiaryFirstName')}
                    maxLength={50}
                  />
                  <ValidationErrorMessage message={errors?.otherBeneficiaryFirstName?.message} />
                </div>
                <div style={{ marginTop: '10px' }}>
                  <div className={`control-group mid  ${errors.otherBeneficiaryLastName && 'error'}`}>
                    <label htmlFor="otherBeneficiaryLastName">{t('loan.otherBeneficiaryLastName')}</label>
                    <input
                      type="text"
                      id="otherBeneficiaryLastName"
                      {...register('otherBeneficiaryLastName')}
                      maxLength={50}
                    />
                    <ValidationErrorMessage message={errors?.otherBeneficiaryLastName?.message} />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {isCoapplicant && (
          <div className="control-group">
            <label htmlFor="relationWithApplicant">{t('personalInformation.relationWithApplicant')}</label>
            <div className={`dropdown-wrap ${errors.applicant?.relationWithApplicant && 'error'}`}>
              <select id="relationWithApplicant" {...register('applicant.relationWithApplicant')}>
                {relationToApplicantOptions}
              </select>
            </div>
          </div>
        )}
      </form>
    </section>
  )
}

export default PersonalInformationForm
